import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AthleteService, AthletePayload } from '../services/athlete.service';
import { AuthService } from '../services/auth.service';
import { WEIGHT_CLASSES } from '../utils/weight-classes';
import { BRAZIL_STATES } from '../utils/brazil-location';
import { LocationService } from '../services/location.service';

@Component({
  selector: 'app-athletes',
  templateUrl: './athletes.component.html',
  styleUrls: ['./athletes.component.scss'],
})
export class AthletesComponent implements OnInit {
  form: FormGroup;
  editForm: FormGroup;
  athletes: any[] = [];
  loading = false;
  errorMessage = '';
  weightClasses = WEIGHT_CLASSES;
  states = BRAZIL_STATES;
  cities: string[] = [];
  countries = [
    'Brasil',
    'Argentina',
    'Uruguai',
    'Chile',
    'Paraguai',
    'Bolívia',
    'Peru',
    'Equador',
    'Colômbia',
    'Venezuela'
  ];
  isAdmin = false;
  showEditModal = false;
  editingAthlete: any = null;
  editCities: string[] = [];
  deletingAthleteId: number | null = null;
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  } | null = null;

  constructor(
    private fb: FormBuilder,
    private athleteService: AthleteService,
    private authService: AuthService,
    private locationService: LocationService
  ) {
    this.form = this.fb.group({
      q: [''],
      gender: [''],
      weight_category: [''],
      country: [''],
      state: [''],
      city: [{ value: '', disabled: true }],
      is_rookie: [''],
      victories_min: [''],
      victories_max: [''],
      age_max: [''],
    });
  }

  ngOnInit(): void {
    this.checkAdminStatus();
    this.search();
    this.initEditForm();
  }

  initEditForm() {
    this.editForm = this.fb.group({
      full_name: ['', Validators.required],
      fight_name: [''],
      birth_date: [''],
      gender: [''],
      weight_category: [''],
      country: ['Brasil'],
      state: [''],
      city: [''],
      is_rookie: [false],
      victories: [0],
      defeats: [0],
      draws: [0],
      notes: [''],
      tapology_link: [''],
      sherdog_link: [''],
      instagram: [''],
      phone: [''],
      email: [''],
    });
  }

  checkAdminStatus() {
    this.authService.getUserInfo().subscribe({
      next: (response) => {
        this.isAdmin = response?.data?.admin ?? false;
      },
      error: () => {
        this.isAdmin = false;
      },
    });
  }

  calculateAge(birthDate: string | null): number | null {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  }

  search(page: number = 1) {
    this.loading = true;
    this.errorMessage = '';
    const params = { ...this.form.value, page };
    this.athleteService.search(params).subscribe({
      next: (response) => {
        const data = response?.data;
        if (data?.data) {
          // Laravel paginated response
          this.athletes = data.data;
          this.pagination = {
            current_page: data.current_page || 1,
            last_page: data.last_page || 1,
            per_page: data.per_page || 15,
            total: data.total || 0,
          };
        } else if (Array.isArray(data)) {
          // Non-paginated response (fallback)
          this.athletes = data;
          this.pagination = null;
        } else {
          this.athletes = [];
          this.pagination = null;
        }
        this.loading = false;
      },
      error: () => {
        this.errorMessage = 'Não foi possível carregar os atletas agora.';
        this.loading = false;
        this.athletes = [];
        this.pagination = null;
      },
    });
  }

  resetFilters() {
    this.form.reset({
      q: '',
      gender: '',
      weight_category: '',
      country: '',
      state: '',
      city: '',
      is_rookie: '',
      victories_min: '',
      victories_max: '',
      age_max: '',
    });
    this.cities = [];
    this.search(1);
  }

  goToPage(page: number) {
    if (page >= 1 && this.pagination && page <= this.pagination.last_page) {
      this.search(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  getPageNumbers(): number[] {
    if (!this.pagination) return [];
    const current = this.pagination.current_page;
    const last = this.pagination.last_page;
    const pages: number[] = [];
    
    // Show max 7 page numbers
    let start = Math.max(1, current - 3);
    let end = Math.min(last, current + 3);
    
    // Adjust if we're near the start or end
    if (end - start < 6) {
      if (start === 1) {
        end = Math.min(last, start + 6);
      } else if (end === last) {
        start = Math.max(1, end - 6);
      }
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  get Math() {
    return Math;
  }

  onStateChange() {
    const uf = this.form.get('state')?.value;
    if (!uf) {
      this.cities = [];
      this.form.get('city')?.reset();
      this.form.get('city')?.disable();
      return;
    }

    this.locationService.getCitiesByState(uf).subscribe({
      next: (cities) => {
        this.cities = cities;
        this.form.get('city')?.enable();
      },
      error: () => {
        this.cities = [];
        this.form.get('city')?.reset();
        this.form.get('city')?.disable();
      },
    });
  }

  logout() {
    this.authService.logout();
  }

  openEditModal(athlete: any) {
    this.editingAthlete = athlete;
    
    // Format birth_date for input[type="date"]
    let formattedBirthDate = '';
    if (athlete.birth_date) {
      const date = new Date(athlete.birth_date);
      if (!isNaN(date.getTime())) {
        formattedBirthDate = date.toISOString().split('T')[0];
      }
    }
    
    this.editForm.patchValue({
      full_name: athlete.full_name || '',
      fight_name: athlete.fight_name || '',
      birth_date: formattedBirthDate,
      gender: athlete.gender || '',
      weight_category: athlete.weight_category || '',
      country: athlete.country || 'Brasil',
      state: athlete.state || '',
      city: athlete.city || '',
      is_rookie: athlete.is_rookie || false,
      victories: athlete.victories || 0,
      defeats: athlete.defeats || 0,
      draws: athlete.draws || 0,
      notes: athlete.notes || '',
      tapology_link: athlete.tapology_link || '',
      sherdog_link: athlete.sherdog_link || '',
      instagram: athlete.instagram || '',
      phone: athlete.phone || '',
      email: athlete.email || '',
    });
    if (athlete.state) {
      this.onEditStateChange(athlete.state);
    }
    this.showEditModal = true;
  }

  closeEditModal() {
    this.showEditModal = false;
    this.editingAthlete = null;
    this.editForm.reset();
    this.editCities = [];
  }

  onEditStateChange(state?: string) {
    const uf = state || this.editForm.get('state')?.value;
    if (!uf) {
      this.editCities = [];
      this.editForm.get('city')?.reset();
      return;
    }

    this.locationService.getCitiesByState(uf).subscribe({
      next: (cities) => {
        this.editCities = cities;
      },
      error: () => {
        this.editCities = [];
      },
    });
  }

  saveAthlete() {
    if (this.editForm.invalid || !this.editingAthlete) {
      this.editForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const formValue = this.editForm.value;
    
    // Format birth_date if provided
    if (formValue.birth_date) {
      formValue.birth_date = new Date(formValue.birth_date).toISOString().split('T')[0];
    }

    this.athleteService.update(this.editingAthlete.id, formValue).subscribe({
      next: () => {
        this.loading = false;
        this.closeEditModal();
        this.search();
      },
      error: () => {
        this.errorMessage = 'Não foi possível atualizar o atleta.';
        this.loading = false;
      },
    });
  }

  confirmDelete(athlete: any) {
    if (confirm(`Tem certeza que deseja excluir o atleta ${athlete.full_name}?`)) {
      this.deleteAthlete(athlete.id);
    }
  }

  deleteAthlete(id: number) {
    this.deletingAthleteId = id;
    this.loading = true;
    this.athleteService.delete(id).subscribe({
      next: () => {
        this.loading = false;
        this.deletingAthleteId = null;
        this.search();
      },
      error: () => {
        this.errorMessage = 'Não foi possível excluir o atleta.';
        this.loading = false;
        this.deletingAthleteId = null;
      },
    });
  }
}

